import { createBrowserRouter, Outlet } from "react-router-dom";
import Root from "../../root";
import * as Features from "../../features";
import { Role } from "../../models";

/**
 * The base router configuration for the application.
 * It defines the routes and their corresponding components.
 */
const browserRouter = createBrowserRouter([
  {
    path: "*",
    element: <Features.NotFound />,
  },
  {
    path: "book",
    element: <Features.PublicPageLayout />,
    children: [
      {
        path: ":locationCode",
        element: <Features.BookingPage />,
      },
      {
        path: ":locationCode/confirmation",
        element: <Features.BookingConfirmationPage />,
      }
    ]
  },
  {
    path: "booking/cancel",
    element: <Features.PublicPageLayout />,
    children: [
      {
        index: true,
        element: <Features.BookingCancelPage />,
      }
    ]
  },
  {
    path: "invoice",
    element: <Features.PublicPageLayout />,
    children: [
      {
        path: ":locationCode",
        element: <Features.InvoicePage />,
      },
    ]
  },
  {
    path: "calendar-fab",
    element: <Features.CalendarLayout />,
    children: [
      {
        path: ":locationCode",
        element: <Features.CalendarPage />,
      },
    ]
  },
  {
    path: "/",
    element: <Root></Root>,
    errorElement: <Features.ErrorPage />,
    children: [
      // Empty path
      {
        path: "",
        element: <Features.HomePage />,
      },
      //Manage
      {
        path: "profile",
        element: <Features.ProfileBaseLayout />,
        children: [
          {
            path: "",
            element: <Features.ProfileIndexPage />,
          },
          {
            path: "change-password",
            element: <Features.ProfileChangePasswordPage />,
          },
        ],
      },
      // User
      {
        path: "user",
        element: (
          <Features.SecurityOutlet
            roles={[
              Role.administrator,
              Role.tenant_admin,
              Role.organization_owner,
              Role.location_manager,
            ]}
          >
            <Outlet />
          </Features.SecurityOutlet>
        ),
        children: [
          {
            path: "",
            element: <Features.UsersIndexLayout />,
            children: [
              {
                path: "",
                element: <Features.UserIndexPage />,
              },
              {
                path: "invite",
                element: <Features.UserInvitationsPage />
              }
            ]
          },
          {
            path: ":id",
            element: <Features.UserLayout />,
            children: [
              {
                path: "",
                element: <Features.UserDetailsPage />,
              },
              {
                path: "access",
                element: <Features.UserAccessPage />,
              },
              {
                path: "delete",
                element: <Features.UserDeletePage />,
              },
            ],
          },
        ],
      },
      // Calendar
      {
        path: "calendar",
        children: [
          {
            path: "",
            element: <Features.CalendarsIndexLayout />,
            children: [
              {
                path: "",
                element: <Features.CalendarIndexPage />,
              },
              {
                path: "location",
                element: <Features.CalendarLocationPage />,
              },
            ]
          },
        ],
      },
      // Appointments
      {
        path: "appointments",
        children: [
          {
            path: "",
            element: <Features.AppointmentsIndexLayout />,
            children: [
              {
                path: "",
                element: <Features.AppointmentsIndexPage />,
              },
            ]
          },
        ],
      },
      // Service Types
      {
        path: "service-type",
        element: (
          <Features.SecurityOutlet
          roles={[
            Role.administrator,
            Role.tenant_admin,
          ]}
          >
            <Outlet />
          </Features.SecurityOutlet>
        ),
        children: [
          {
            path: "",
            element: <Features.ServiceTypesIndexLayout />,
            children: [
              {
                path: "",
                element: <Features.ServiceTypesIndexPage />
              }
            ]
          },
          {
            path: ":id",
            element: <Features.ServiceTypeLayout />,
            children: [
              {
                path: "",
                element: <Features.ServiceTypeDetailsPage />,
              },
              {
                path: "service",
                element: <Features.ServiceTypeServicesPage />,
              }
            ]
          }
        ]
      },
      // Locations
      {
        path: "location",
        element: (
          <Features.SecurityOutlet
          roles={[
            Role.administrator,
            Role.tenant_admin,
            Role.organization_owner,
            Role.location_manager,
          ]}
          >
            <Outlet />
          </Features.SecurityOutlet>
        ),
        children: [
          {
            path: "",
            element: <Features.LocationsIndexLayout />,
            children: [
              {
                path: "",
                element: <Features.LocationsIndexPage />
              }
            ]
          },
          {
            path: ":id",
            element: <Features.LocationLayout />,
            children: [
              {
                path: "",
                element: <Features.LocationDetailsPage />,
              },
              {
                path: "address",
                element: <Features.LocationAddressPage />,
              },
              {
                path: "pricing",
                element: <Features.LocationPricingPage />,
              },
              {
                path: "closure",
                element:<Features.LocationClosurePage />
              },
              {
                path: "payments",
                element: <Features.LocationPaymentsPage />,
              },
              {
                path: "provider",
                element: <Features.LocationProvidersPage />,
              },
              {
                path: "provider/:providerId",
                element: <Features.ProviderLayout />,
                children: [
                  {
                    path: "",
                    element: <Features.ProviderDetailsPage />,
                  },
                  {
                    path: "services",
                    element: <Features.ProviderServicesPage />,
                  },
                  {
                    path: "schedule",
                    element: <Features.ProviderSchedulePage />,
                  }
                ]
              }
            ]
          }
        ]
      },
      // Organizations
      {
        path: "organization",
        element: (
          <Features.SecurityOutlet
            roles={[
              Role.administrator,
              Role.tenant_admin,
              Role.organization_owner
            ]}
          >
            <Outlet />
          </Features.SecurityOutlet>
        ),
        children: [
          {
            path: "",
            element: <Features.OrganizationsIndexLayout/>,
            children: [
              {
                path: "",
                element: <Features.OrganizationsIndexPage />
              }
            ]
          },
          {
            path: ":id",
            element: <Features.OrganizationLayout />,
            children: [
              {
                path: "",
                element: <Features.OrganizationDetailsPage />,
              },
              {
                path:"address",
                element: <Features.OrganizationAddressPage />,
              }
            ]
          }
        ]
      },
      // host
      {
        path: "host",
        element: (
          <Features.SecurityOutlet
            roles={[
              Role.administrator
            ]}
          >
            <Outlet />
          </Features.SecurityOutlet>
        ),
        children: [
          {
            path: "",
            element: <Features.HostIndexLayout/>,
            children: [
              {
                path: "",
                element: <Features.HostIndexPage />
              },
              {
                path: "tenant",
                element: <Features.HostTenantsPage />,
              },
              {
                path: "subscription",
                element: <Features.HostSubscriptionsPage />,
              },
            ]
          },
          {
            path: "",
            element: <Features.HostTenantLayout/>,
            children: [
              {
                path: "tenant/:id",
                element: <Features.HostTenantDetailsPage />
              },
              {
                path: "tenant/:id/organization",
                element: <Features.HostTenantOrganizationPage />
              },
              {
                path: "tenant/:id/features",
                element: <Features.HostTenantFeaturesPage />
              }
            ]
          },
          {
            path: "",
            element: <Features.SubscriptionLayout/>,
            children: [
              {
                path: "subscription/:id",
                children: [
                  {
                    path: "",
                    element: <Features.SubscriptionDetailsPage />,
                  },
                  {
                    path: "billing-history",
                    element: <Features.SubscriptionItemsPage />,
                  },
                  {
                    path: "billing-info",
                    element: <Features.SubscriptionBillingInfoPage />,
                  },
                ]
              }
            ]
          },
        ]
      },
    ],
  },
  //Account
  {
    path: "account",
    element: <Features.AccountPageLayout />,
    errorElement: <Features.ErrorPage />,
    children: [
      {
        path: "",
        element: <Features.LoginPage />,
      },
      {
        path: "register/:id/:key",
        element: <Features.RegisterPage />,
      },
      {
        path: "register/confirmation",
        element: <Features.RegisterConfirmationPage />,
      },
      {
        path: "login",
        element: <Features.LoginPage />,
      },
      {
        path: "tenant-selection",
        element: <Features.TenantSelectionPage />,
      },
      {
        path: `login?returnUrl=:returnUrl`,
        element: <Features.LoginPage />,
      },
      {
        path: "forgotPassword",
        element: <Features.ForgotPasswordPage />,
      },
      {
        path: "forgotPasswordConfirmation",
        element: <Features.ForgotPasswordConfirmationPage />,
      },
      {
        path: "resetPassword",
        element: <Features.ResetPasswordPage />,
      },
      {
        path: "resetPasswordConfirmation",
        element: <Features.ResetPasswordConfirmationPage />,
      },
      {
        path: "registrationRequest",
        element: <Features.RegistrationRequestPage />,
      },
      {
        path: "registrationRequestConfirmation",
        element: <Features.RegistrationRequestConfirmationPage />,
      },
    ],
  },
]);

/**
 * The router object that extends the baseRouter and provides additional functionality.
 */
export const router = Object.assign(browserRouter, {
  /**
   * Gets the URL with the specified parameters replaced.
   * @param url - The URL to process.
   * @param params - The parameters to replace in the URL.
   * @returns The processed URL.
   */
  getUrl: (url: string, params: Map<string, string> | undefined) => {
    let lowerUrl = url.toLocaleLowerCase();
    let replacedUrl: string = lowerUrl;
    params?.forEach((value, key) => {
      replacedUrl = lowerUrl.replace(`:${key.toLocaleLowerCase()}`, value);
      lowerUrl = replacedUrl;
    });
    return lowerUrl;
  },
  /**
   * Navigates to the specified URL with the specified parameters.
   * @param url - The URL to navigate to.
   * @param params - The parameters to replace in the URL.
   * @param state - The state to pass to the new URL.
   * @param newTab - A boolean indicating whether to open the URL in a new tab.
   */
  navigateWithParams: (
    url: string,
    params: Map<string, string> | undefined,
    state: any = undefined,
    newTab: boolean = false
  ) => {
    const processedUrl = router.getUrl(url, params);
    if (newTab) {
      window.open(processedUrl, "_blank");
      return;
    }
    browserRouter.navigate(processedUrl, state).catch((error) => {
      return <Features.ErrorPage />;
    });
  },
});

export default router;
